import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaExpand, FaLightbulb, FaCalculator } from 'react-icons/fa';

const ThermalExpansionCalculator = () => {
  const [initialLength, setInitialLength] = useState('');
  const [initialTemperature, setInitialTemperature] = useState('');
  const [finalTemperature, setFinalTemperature] = useState('');
  const [coefficient, setCoefficient] = useState('');
  const [finalLength, setFinalLength] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateExpansion = () => {
    if (initialLength && initialTemperature && finalTemperature && coefficient) {
      const L0 = parseFloat(initialLength);
      const T0 = parseFloat(initialTemperature);
      const Tf = parseFloat(finalTemperature);
      const α = parseFloat(coefficient);
      
      // Calculate final length using thermal expansion formula
      const Lf = L0 * (1 + α * (Tf - T0));
      setFinalLength(Lf.toFixed(3));
      setShowGraph(true);
    }
  };

  const generateGraphData = () => {
    if (!initialLength || !initialTemperature || !coefficient) return null;

    const L0 = parseFloat(initialLength);
    const T0 = parseFloat(initialTemperature);
    const α = parseFloat(coefficient);
    
    // Generate data for length vs temperature
    const series = [
      {
        name: 'Length vs Temperature',
        data: Array.from({ length: 20 }, (_, i) => {
          const currentTemperature = T0 + (i - 10);
          const currentLength = L0 * (1 + α * (currentTemperature - T0));
          return {
            x: currentTemperature,
            y: currentLength
          };
        }),
      },
    ];

    const options = {
      chart: {
        type: 'line',
        height: 400,
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      xaxis: {
        type: 'numeric',
        title: {
          text: 'Temperature (°C)',
        },
        labels: {
          formatter: function (val) {
            return val.toFixed(1);
          }
        }
      },
      yaxis: {
        title: {
          text: 'Length (m)',
        },
        labels: {
          formatter: function (val) {
            return val.toFixed(3);
          }
        }
      },
      title: {
        text: 'Length Change with Temperature',
        align: 'center',
      },
      markers: {
        size: 4,
        colors: ['#FF4560'],
      },
      annotations: {
        yaxis: [
          {
            y: L0,
            borderColor: '#FF4560',
            label: {
              borderColor: '#FF4560',
              style: {
                color: '#fff',
                background: '#FF4560',
              },
              text: 'Initial Length = ' + L0 + 'm'
            }
          },
          {
            y: parseFloat(finalLength),
            borderColor: '#FF4560',
            label: {
              borderColor: '#FF4560',
              style: {
                color: '#fff',
                background: '#FF4560',
              },
              text: 'Final Length = ' + parseFloat(finalLength).toFixed(3) + 'm'
            }
          }
        ]
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val.toFixed(3) + 'm';
          }
        }
      }
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-100 to-cyan-100">
      <Helmet>
        <title>Thermal Expansion Calculator | Physics Tool</title>
        <meta name="description" content="Calculate thermal expansion of materials. Interactive calculator with graph visualization showing length change with temperature." />
        <meta name="keywords" content="thermal expansion calculator, physics calculator, material expansion calculator, temperature effects calculator" />
        <meta property="og:title" content="Thermal Expansion Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate thermal expansion of materials. Interactive calculator with graph visualization showing length change with temperature." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Thermal Expansion Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate thermal expansion of materials. Interactive calculator with graph visualization showing length change with temperature." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/thermal-expansion" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-blue-600 mb-4">
            <FaExpand />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Thermal Expansion Calculator</h1>
          <p className="text-xl text-gray-600">Calculate material expansion with temperature</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Initial Length (m)</label>
                <input
                  type="number"
                  value={initialLength}
                  onChange={(e) => setInitialLength(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter initial length (m)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Initial Temperature (°C)</label>
                <input
                  type="number"
                  value={initialTemperature}
                  onChange={(e) => setInitialTemperature(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter initial temperature (°C)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Final Temperature (°C)</label>
                <input
                  type="number"
                  value={finalTemperature}
                  onChange={(e) => setFinalTemperature(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter final temperature (°C)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Coefficient of Thermal Expansion (1/°C)</label>
                <input
                  type="number"
                  value={coefficient}
                  onChange={(e) => setCoefficient(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter coefficient (1/°C)"
                />
              </div>
              <div>
                <button
                  onClick={calculateExpansion}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Expansion
                </button>
              </div>

              {finalLength && (
                <div className="mt-4 p-4 bg-blue-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-blue-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-blue-800">Result</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-blue-800">Final Length = {finalLength} m</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-yellow-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using the thermal expansion formula:
                      <br />
                      Lf = L0 × (1 + α × (Tf - T0))
                      <br />
                      <br />
                      Where:
                      <br />
                      Lf = Final length = {finalLength} m
                      <br />
                      L0 = Initial length = {initialLength} m
                      <br />
                      α = Coefficient = {coefficient} 1/°C
                      <br />
                      T0 = Initial temperature = {initialTemperature}°C
                      <br />
                      Tf = Final temperature = {finalTemperature}°C
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Length vs Temperature</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="line"
                height={400}
              />
              <p className="mt-4 text-gray-600 text-center">
                Shows how length changes with temperature
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the initial length of the material (m)</li>
            <li>Enter the initial temperature (°C)</li>
            <li>Enter the final temperature (°C)</li>
            <li>Enter the coefficient of thermal expansion (1/°C)</li>
            <li>Click "Calculate Expansion" to get the result</li>
            <li>View the interactive graph showing length change</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Accurate Calculations</h3>
              <p>Calculate thermal expansion using the standard formula</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Graph</h3>
              <p>Visualize how length changes with temperature</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for physics and materials science students</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Step-by-Step Solutions</h3>
              <p>See how calculations are performed</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is Thermal Expansion?</h3>
              <p className="text-gray-600">
                Thermal expansion is the tendency of matter to change shape, area, and volume in response to a change in temperature.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What units should I use?</h3>
              <p className="text-gray-600">
                For accurate calculations:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Length should be in meters (m)</li>
                  <li>Temperature should be in Celsius (°C)</li>
                  <li>Coefficient should be in 1/°C</li>
                  <li>The result will be in meters (m)</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">How does the graph work?</h3>
              <p className="text-gray-600">
                The graph shows length change with temperature:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>X-axis represents temperature (°C)</li>
                  <li>Y-axis represents length (m)</li>
                  <li>The line shows how length changes with temperature</li>
                  <li>Markers show the initial and final lengths</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                Thermal expansion calculations are important in many areas:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Building construction</li>
                  <li>Bridge design</li>
                  <li>Pipe systems</li>
                  <li>Material selection for high-temperature applications</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Engineers design structures that can handle temperature changes</li>
                  <li>Students understand material behavior under different temperatures</li>
                  <li>Teachers demonstrate thermal expansion concepts</li>
                  <li>Anyone needing to calculate material expansion</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThermalExpansionCalculator;
